import { Component, EventEmitter, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../auth/services/auth.service';
import { RoleEnum } from '../../shared/contracts/role.interface';
import { SpeedDialModule } from 'primeng/speeddial';
import { ProfileComponent } from '../../users/features/profile/profile.component';
import { filter } from 'rxjs/operators';

interface NavItem {
  icon: string;
  title: string;
  url: string;
  roles?: RoleEnum[];
  click: () => any;
}

@Component({
  selector: 'app-sidebar-menu',
  standalone: true,
  imports: [CommonModule, AvatarModule, DividerModule, TooltipModule, SpeedDialModule],
  styleUrls: ['./sidebar-menu.component.scss'],
  templateUrl: './sidebar-menu.component.html',
})
export class SidebarMenuComponent implements OnInit {
  isExpanded = signal(true);

  navigationItems: NavItem[] = [];
  userRole?: RoleEnum;
  selectedNavItem: NavItem | null = null;
  filteredAdditionalNavItems: NavItem[] = [];

  userProfile = {
    name: 'User Name',
    email: 'user@example.com',
    imageMediaUrl: './assets/profile/2d-avatar.jpg',
    allowSelfAssignment: false,
  };

  navItemSelected = new EventEmitter<string>();
  additionalItemClicked = new EventEmitter<NavItem>();

  allNavigationItems: NavItem[] = [
    { icon: 'fa-light fa-house-blank', title: 'Dashboard', url: 'dashboard', roles: [RoleEnum.SUPERVISOR], click: () => null },
    { icon: 'fa-light fa-building', title: 'Aziende', url: 'tenants', roles: [RoleEnum.ADMIN], click: () => null },
    { icon: 'fa-light fa-inbox', title: 'Assegnazioni', url: 'assignments', roles: [RoleEnum.SUPERVISOR], click: () => null },
    { icon: 'fa-light fa-message', title: 'Conversazioni', url: 'conversations', roles: [RoleEnum.SUPERVISOR, RoleEnum.OPERATOR], click: () => null },
    { icon: 'fa-light fa-user', title: 'Operatori', url: 'users', roles: [RoleEnum.ADMIN, RoleEnum.SUPERVISOR], click: () => null },
    { icon: 'fa-light fa-address-book', title: 'Contatti', url: 'contacts', roles: [RoleEnum.SUPERVISOR], click: () => null },
    { icon: 'fa-light fa-users', title: 'Teams', url: 'teams', roles: [RoleEnum.SUPERVISOR], click: () => null },
    { icon: 'fa-light fa-message-quote', title: 'Templates', url: 'templates', roles: [RoleEnum.SUPERVISOR], click: () => null },
  ];

  additionalNavItems: NavItem[] = [
    { icon: 'fa-light fa-arrow-right-from-bracket', title: 'Logout', url: 'logout', roles: [RoleEnum.SUPERVISOR, RoleEnum.ADMIN, RoleEnum.OPERATOR], click: () => this.logout() },
    { icon: 'fa-light fa-gear-complex', title: 'Settings', url: 'settings', roles: [RoleEnum.SUPERVISOR], click: () => this.navigateToSettings() },
    { icon: 'fa-light fa-circle-user', title: 'Profilo', url: 'profile', roles: [RoleEnum.SUPERVISOR, RoleEnum.ADMIN, RoleEnum.OPERATOR], click: () => this.navigateToProfile() },
  ];

  constructor(private router: Router, private authService: AuthService) {}

  ngOnInit(): void {
    this.loadUserProfile();
    this.userRole = this.authService.getUserRole()!;
    this.filterNavigationItems();
    this.filterAdditionalNavItems();

    this.setSelectedByUrl(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.setSelectedByUrl(event.urlAfterRedirects || event.url);
      });

    ProfileComponent.avatarChanged.subscribe((newImageUrl: string) => {
      this.userProfile.imageMediaUrl = newImageUrl;
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      stored.imageMediaUrl = newImageUrl;
      localStorage.setItem('userData', JSON.stringify(stored));
    });
  }

  toggleSidebar(): void {
    this.isExpanded.update(v => !v);
  }

  private filterAdditionalNavItems(): void {
    if (!this.userRole) {
      this.filteredAdditionalNavItems = [];
      return;
    }

    this.filteredAdditionalNavItems = this.additionalNavItems.filter(item => {
      if (!item.roles) return true;
      return item.roles.includes(this.userRole!);
    });
  }

  private loadUserProfile(): void {
    const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
    this.userProfile = {
      ...this.userProfile,
      name: [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ') || storedUser.username || this.userProfile.name,
      email: storedUser.email || this.userProfile.email,
      imageMediaUrl: storedUser.imageMediaUrl || this.userProfile.imageMediaUrl,
      allowSelfAssignment: storedUser.allowSelfAssignment || false,
    };
  }

  private filterNavigationItems(): void {
    if (!this.userRole) {
      this.navigationItems = [];
      return;
    }

    this.navigationItems = this.allNavigationItems.filter(item => {
      if (!item.roles) return true;

      if (
        this.userRole === RoleEnum.OPERATOR &&
        item.url === 'assignments' &&
        this.userProfile.allowSelfAssignment
      ) {
        return true;
      }

      return item.roles.includes(this.userRole!);
    });
  }

  private setSelectedByUrl(url: string): void {
    const cleanUrl = url.replace(/^\//, '');
    const found =
      [...this.navigationItems, ...this.additionalNavItems].find(item => cleanUrl.startsWith(item.url));
    this.selectedNavItem = found || null;
  }

  selectNavItem(navItem: NavItem): void {
    this.selectedNavItem = navItem;
    this.router.navigate([navItem.url]);
  }

  selectAdditionalNavItem(navItem: NavItem): void {
    if (navItem.title !== 'Logout') {
      this.selectedNavItem = navItem;
    }
    navItem.click();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  navigateToProfile(): void {
    this.router.navigate(['/profile']);
  }

  navigateToSettings(): void {
    this.router.navigate(['/settings']);
  }
}
